//Libs
import { XMarkIcon } from "@heroicons/react/24/solid";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";

//Local
import * as strings from "../../strings";
import BaseModal from "../BaseModal";
import HeadingOne from "../HeadingOne";
import { ExistingVendor } from "../../models/vendor";
import VendorFormWithAutocomplete, {
  VendorFormValues,
} from "./VendorFormWithAutocomplete";
import VendorFormManual from "./VendorFormManual";
import BaseButtonSecondary from "../BaseButtonSecondary";

interface Props {
  /* DATA */
  isDialogOpen: boolean;
  vendor?: ExistingVendor; // Optional - if provided, we're editing; if not, we're creating
  geocoderResult: google.maps.GeocoderResult;
  showManualEntryForm: boolean;
  /* FUNCTIONS */
  closeDialog: () => void;
  handleSave: (formValues: VendorFormValues) => Promise<void>;
  setShowManualEntryForm: (show: boolean) => void;
  /* LOGO UPLOAD */
  children?: {
    VendorLogoSection?: React.ReactNode;
    AddressField: React.ReactNode;
  };
}

export default function VendorDialog(props: Props) {
  const isEditing = !!props.vendor;
  const title = isEditing ? strings.EDIT_VENDOR : strings.CREATE_VENDOR;

  const vendorHeader = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white ">
      <HeadingOne fontColor="text-white" fontSize="text-xl">
        {title}
      </HeadingOne>
      <button type="button" onClick={() => props.closeDialog()}>
        <XMarkIcon
          aria-label={`close ${isEditing ? "edit" : "new"} vendor form`}
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  return (
    <BaseModal
      closeModal={() => {}}
      open={props.isDialogOpen}
      title={vendorHeader}
      parentDivStyles="inline-block transform overflow-hidden max-w-screen-md rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="space-y-8 p-8">
        {props.showManualEntryForm ? (
          <VendorFormManual
            className="flex flex-col space-y-8 text-lg"
            handleSave={props.handleSave}
            setVendorFormOpen={props.closeDialog}
            vendor={props.vendor}
          >
            {{
              VendorLogoSection: props.children?.VendorLogoSection,
            }}
          </VendorFormManual>
        ) : (
          <VendorFormWithAutocomplete
            className="flex flex-col space-y-8 text-lg"
            handleSave={props.handleSave}
            setVendorFormOpen={props.closeDialog}
            vendor={props.vendor}
            geocoderResult={props.geocoderResult}
          >
            {{
              VendorLogoSection: props.children?.VendorLogoSection,
              AddressField: (
                <div className="flex w-full items-center gap-4">
                  {props.children?.AddressField}
                  <BaseButtonSecondary
                    type="button"
                    onClick={() => {
                      props.setShowManualEntryForm(true);
                    }}
                    className="w-full text-primary sm:w-56"
                  >
                    <PencilIcon
                      aria-label="manual entry"
                      className="mr-2 h-4"
                    />
                    {strings.MANUAL_ENTRY}
                  </BaseButtonSecondary>
                </div>
              ),
            }}
          </VendorFormWithAutocomplete>
        )}
      </div>
    </BaseModal>
  );
}
